import React, { useEffect, useRef, useState } from 'react';
import jm, { formatDateTime, photoUrl, referredBy, toInputDate } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import RatingRow from '../common/RatingRow';
import CandidateSummary, {
  PHONE_SCORE_FIELDS, ScoreTable, SectionHeader,
} from '../common/CandidateSummary';
import useStage from '../common/useStage';
import { EmployeeSelect, useMasters } from '../common/MastersContext';
import DateField from '../common/DateField';

const NEXT_STATUS = [
  { value: 'Demo', label: 'Demo (stay here)' },
  { value: 'Interviewed', label: 'Personal Interview 1' },
  { value: 'hold', label: 'Hold' },
  { value: 'Rejected', label: 'Rejected' },
];

const RATINGS = [
  { name: 'content', label: 'Content' },
  { name: 'professional', label: 'Professional' },
  { name: 'properties', label: 'Properties' },
  { name: 'voice', label: 'Voice' },
];

const Demo = ({ onMoved }) => {
  const masters = useMasters();
  const [form, setForm] = useState({});
  const resumeRef = useRef(null);
  const photoRef = useRef(null);

  const { rows, loading, editing, detail, detailLoading, openEdit, closeEdit, saving, save } =
    useStage(jm.demo, jm.saveDemo, { onMoved });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!detail) return;
    const app = detail.application;
    const stage = detail.demo || {};

    setForm({
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      email: app.email || '',
      dob: toInputDate(app.dob),
      contact_no: app.contact_no || '',
      content: Number(stage.content) || 0,
      professional: Number(stage.professional) || 0,
      properties: Number(stage.properties) || 0,
      voice: Number(stage.voice) || 0,
      demo_notes: stage.notes || '',
      job_status: 'Demo',
      interview_date: '',
      interview_time: '',
      interview_by: '',
      first_report: '',
      interview_notes: '',
      hold_notes: '',
      reject_notes: '',
    });
  }, [detail]);

  const liveScore = RATINGS.reduce((sum, field) => sum + (Number(form[field.name]) || 0), 0);

  const submit = () =>
    save({
      ...form,
      resume_file: resumeRef.current?.files?.[0],
      p_file: photoRef.current?.files?.[0],
    });

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status', search: false, sortable: false, render: () => <StatusBadge label="Demo" /> },
    { key: 'done_on', label: 'Demo On', render: (row) => formatDateTime(row.done_on) },
  ];

  return (
    <>
      <CandidateTable title="Demo" rows={rows} columns={columns} loading={loading} exportName="demo_candidates" />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={submit}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <CandidateSummary candidate={detail.application} />

            <ScoreTable
              title="Phone Interview"
              fields={PHONE_SCORE_FIELDS}
              row={detail.shortlisted}
              total={detail.scores.phone}
            />

            <div className="border rounded p-3 mb-3">
              <SectionHeader title="Demo" score={liveScore} max={20} />

              {RATINGS.map((field) => (
                <RatingRow
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  value={form[field.name]}
                  onChange={set}
                />
              ))}

              <div className="form-group mt-2">
                <label>Demo Notes</label>
                <textarea className="form-control" rows="2" value={form.demo_notes || ''} onChange={(e) => set('demo_notes', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>Job Status</label>
                <select className="form-control required-input" value={form.job_status || ''} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.job_status === 'Interviewed' && (
              <div className="border rounded p-3 mb-3">
                <SectionHeader title="Personal Interview Schedule" />
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label>Interview Date</label>
                    <DateField value={form.interview_date || ''} onChange={(v) => set('interview_date', v)} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label>Interview Time</label>
                    <input type="time" className="form-control" value={form.interview_time || ''} onChange={(e) => set('interview_time', e.target.value)} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label>Interview By</label>
                    <EmployeeSelect value={form.interview_by} onChange={(v) => set('interview_by', v)} employees={masters.employees} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label>First Level Reporting</label>
                    <EmployeeSelect value={form.first_report} onChange={(v) => set('first_report', v)} employees={masters.employees} />
                  </div>
                  <div className="col-md-12 form-group">
                    <label>Interview Notes</label>
                    <textarea className="form-control" rows="2" value={form.interview_notes || ''} onChange={(e) => set('interview_notes', e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            {form.job_status === 'hold' && (
              <div className="form-group">
                <label>Hold Notes</label>
                <textarea className="form-control" rows="2" value={form.hold_notes || ''} onChange={(e) => set('hold_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'Rejected' && (
              <div className="form-group">
                <label>Reject Notes</label>
                <textarea className="form-control" rows="2" value={form.reject_notes || ''} onChange={(e) => set('reject_notes', e.target.value)} />
              </div>
            )}

            <h6 className="mt-4 mb-3">Edit Candidate</h6>
            <div className="row">
              <div className="col-md-6 form-group">
                <label>First Name</label>
                <input className="form-control" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Last Name</label>
                <input className="form-control" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Date of Birth</label>
                <DateField value={form.dob || ''} onChange={(v) => set('dob', v)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Contact Number</label>
                <input className="form-control" maxLength={10} value={form.contact_no || ''} onChange={(e) => set('contact_no', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Email</label>
                <input className="form-control" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Resume</label>
                <input type="file" className="form-control-file" ref={resumeRef} accept=".pdf,.doc,.docx" />
              </div>
              <div className="col-md-6 form-group">
                <label>Photo</label>
                <input type="file" className="form-control-file" ref={photoRef} accept="image/*" />
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default Demo;
